<template>
  <div>
    <!-- Trigger: active workspace name, opens the list -->
    <Button
      id="btn-workspace-switcher"
      ref="triggerRef"
      severity="secondary"
      size="small"
      class="min-w-0 max-w-[220px]"
      aria-haspopup="true"
      :aria-expanded="open"
      @click="togglePopover"
    >
      <span class="material-icons shrink-0 text-cyan-400" aria-hidden="true">domain</span>
      <span class="truncate flex-1 text-left font-medium">{{ store.currentWorkspace?.name ?? '...' }}</span>
      <span
        class="material-icons shrink-0 text-gray-400 transition-transform"
        :class="{ 'rotate-180': open }"
        aria-hidden="true"
      >expand_more</span>
    </Button>

    <!-- Dropdown: workspace list + "new workspace" footer -->
    <Popover
      ref="popoverRef"
      :aria-label="$t('workspace.switcher.listLabel')"
      :dt="popoverDt"
      :pt="popoverPt"
      @show="onPopoverShow"
      @hide="onPopoverHide"
    >
      <!-- v-if: no untranslated PrimeVue "No available options" row while the list loads.
           metaKeySelection: a click on the active row (or on its rename controls)
           must not deselect it. highlightOnSelect off: the active row is marked by
           its own icon and text below, as before. -->
      <Listbox
        v-if="store.workspaces.length"
        ref="listboxRef"
        id="workspace-list"
        :modelValue="store.currentWorkspace"
        :options="store.workspaces"
        optionLabel="name"
        dataKey="_id"
        metaKeySelection
        :highlightOnSelect="false"
        scrollHeight="16rem"
        :ariaLabel="$t('workspace.switcher.listLabel')"
        :dt="listboxDt"
        :pt="listboxPt"
        @change="onWorkspaceChange"
      >
        <template #option="{ option }">
          <div class="flex flex-1 items-center gap-2 min-w-0 min-h-8 text-sm">
            <span
              class="material-icons shrink-0 select-none"
              :class="isCurrent(option) ? 'text-cyan-400' : 'text-gray-500'"
              aria-hidden="true"
            >{{ isCurrent(option) ? 'check_circle' : 'radio_button_unchecked' }}</span>

            <!-- Inline rename (active workspace only). @keydown.stop keeps typing away
                 from the Listbox's keyboard navigation and Esc from closing the Popover.
                 Enter/Esc act on keydown: a rename started with Enter on the pencil would
                 otherwise get that same Enter's keyup here and close itself. -->
            <template v-if="isCurrent(option) && renaming">
              <InputText
                id="input-rename-workspace"
                ref="renameInputRef"
                v-model="renameValue"
                size="small"
                class="flex-1 min-w-0"
                :aria-label="$t('workspace.namePlaceholder')"
                @keydown.stop
                @keydown.enter="!$event.repeat && saveRename()"
                @keydown.escape="cancelRename"
                @blur="saveRename"
              />
              <!-- @mousedown.prevent keeps the input focused, so its blur doesn't
                   save before the click: Save saves once, Cancel really cancels. -->
              <Button
                id="btn-save-rename-workspace"
                text
                rounded
                size="small"
                class="shrink-0"
                v-tooltip="$t('common.save')"
                :aria-label="$t('common.save')"
                @mousedown.prevent
                @click="saveRename"
              >
                <template #icon>
                  <span class="material-icons" style="font-size: 18px">check</span>
                </template>
              </Button>
              <Button
                id="btn-cancel-rename-workspace"
                text
                rounded
                severity="secondary"
                size="small"
                class="shrink-0"
                v-tooltip="$t('common.cancel')"
                :aria-label="$t('common.cancel')"
                @mousedown.prevent
                @click="cancelRename"
              >
                <template #icon>
                  <span class="material-icons" style="font-size: 18px">close</span>
                </template>
              </Button>
            </template>

            <template v-else>
              <span
                class="truncate flex-1 select-none"
                :class="isCurrent(option) ? 'text-white font-medium' : 'text-gray-300'"
              >{{ option.name }}</span>
              <!-- Rename pencil: active workspace only, shown on row hover / keyboard focus.
                   Enter/Space stop here, or the Listbox would preventDefault them
                   and the button would never activate from the keyboard. -->
              <Button
                v-if="isCurrent(option)"
                id="btn-rename-workspace"
                text
                rounded
                severity="secondary"
                size="small"
                class="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                v-tooltip="$t('workspace.switcher.rename')"
                :aria-label="$t('workspace.switcher.rename')"
                @keydown.enter.stop
                @keydown.space.stop
                @click="startRename(option)"
              >
                <template #icon>
                  <span class="material-icons" style="font-size: 18px">edit</span>
                </template>
              </Button>
            </template>
          </div>
        </template>
      </Listbox>

      <!-- Footer: new workspace (admin/partner only) -->
      <div v-if="canCreateWorkspaces" class="border-t border-gray-700 p-1">
        <Button
          id="btn-request-create-workspace"
          ref="createButtonRef"
          text
          severity="secondary"
          size="small"
          fluid
          :pt="createButtonPt"
          @click="openCreateModal"
        >
          <span class="material-icons" aria-hidden="true">add</span>
          {{ $t('workspace.createNew') }}
        </Button>
      </div>
    </Popover>

    <!-- Create workspace. Opened from the Popover footer: openCreateModal hides the
         Popover first, then shows this; the Dialog focuses the [autofocus] name field.
         X / Esc / mask click run closeCreateModal, same as Cancel; all of them are
         blocked while the request runs. -->
    <Dialog
      id="create-workspace-modal"
      :visible="showCreateModal"
      modal
      :header="$t('workspace.createTitle')"
      :draggable="false"
      :closable="!creating"
      :closeOnEscape="!creating"
      :dismissableMask="!creating"
      :style="{ width: '24rem' }"
      :breakpoints="{ '640px': '95vw' }"
      @update:visible="(visible) => { if (!visible) closeCreateModal(); }"
    >
      <!-- The submit Button lives in the Dialog footer (outside this form), so it is
           tied back to the form with form="create-workspace-form"; Enter still submits. -->
      <form id="create-workspace-form" @submit.prevent="createWorkspace">
        <InputText
          id="input-new-workspace-name"
          v-model="newName"
          class="w-full"
          :placeholder="$t('workspace.namePlaceholder')"
          :aria-label="$t('workspace.namePlaceholder')"
          autofocus
        />
      </form>

      <template #footer>
        <Button
          id="btn-cancel-create-workspace"
          type="button"
          severity="secondary"
          :label="$t('common.cancel')"
          :disabled="creating"
          @click="closeCreateModal"
        />
        <Button
          id="btn-confirm-create-workspace"
          type="submit"
          form="create-workspace-form"
          :label="creating ? $t('common.saving') : $t('common.create')"
          :loading="creating"
          :disabled="!newName.trim() || creating"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
/**
 * WorkspaceSwitcher.vue
 *
 * Header control showing the active workspace. It lets the user switch workspace,
 * rename the active one inline, and (admins/partners) create a new one.
 *
 * - Trigger: PrimeVue Button (secondary, small) with the workspace name.
 * - List: a PrimeVue Popover holding a Listbox of `store.workspaces`. Picking another
 *   workspace calls `select()`: it switches the store, closes the Popover and opens
 *   that workspace's bot list. The Popover handles outside clicks and Esc.
 * - Inline rename (active row): InputText plus icon Buttons. Enter or blur saves,
 *   Esc or the cancel button discards. Closing the Popover drops an unfinished rename.
 * - Create (admins/partners): the Popover footer action opens a PrimeVue Dialog with a
 *   name InputText. Enter submits; Cancel, X, Esc and a mask click close it, all
 *   blocked while the request runs.
 *
 * No props or events: all state comes from the workspace store.
 *
 * @example
 * <WorkspaceSwitcher />
 */
import { ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { useUserRole } from '../../composables/useUserRole';

const store = useWorkspaceStore();
const router = useRouter();
const { canCreateWorkspaces } = useUserRole();

// Mirrors the Popover visibility (chevron rotation, aria-expanded)
const open = ref(false);
const popoverRef = ref(null);
const triggerRef = ref(null);
const listboxRef = ref(null);
const createButtonRef = ref(null);
// Whether the last toggle came from the keyboard (Enter/Space click has detail 0)
let openedByKeyboard = false;

// Popover panel: no inner padding, the Listbox list and the footer bring their own.
const popoverDt = { content: { padding: '0' } };
const popoverPt = { root: { class: 'w-72' } };

// The Listbox sits flush inside the Popover panel (no second border, background or
// shadow). With the `min-h-8` row content, the option padding keeps the old row height.
const listboxDt = {
  root: { background: 'transparent', borderColor: 'transparent', shadow: 'none' },
  option: { padding: '0.25rem 0.75rem' }
};
// `group` drives the hover-only rename button; the telemetry id lands on each <li>.
const listboxPt = { option: { class: 'group', 'data-telemetry-id': 'workspace-list-item' } };

// PrimeVue centres Button content and its CSS is unlayered (cssLayer: false), so a
// Tailwind justify-* class can't left-align the full-width footer action.
const createButtonPt = { root: { style: { justifyContent: 'flex-start' } } };

// Create
const showCreateModal = ref(false);
const newName = ref('');
const creating = ref(false);

// Rename
const renaming = ref(false);
const renameValue = ref('');
const renameInputRef = ref(null);

function isCurrent(ws) {
  return ws._id === store.currentWorkspace?._id;
}

function togglePopover(event) {
  openedByKeyboard = event.detail === 0;
  popoverRef.value?.toggle(event);
}

// The Popover is teleported to the end of <body> and only focuses an [autofocus]
// element, so a keyboard user would stay on the trigger. Move focus into the panel:
// focusing the Listbox's list puts the focused option on the active row (Arrows/Enter
// work from there); with no list, the "new workspace" action. Mouse opens don't move focus.
function onPopoverShow() {
  open.value = true;
  if (!openedByKeyboard) return;
  const list = listboxRef.value?.$el?.querySelector('[role="listbox"]');
  if (list) list.focus();
  else createButtonRef.value?.$el?.focus();
}

// Closing the list (outside click, Esc, selection) drops an unfinished rename, as the
// old click-outside handler did. A blur that fired first has already saved it.
function onPopoverHide() {
  open.value = false;
  cancelRename();
}

// Listbox @change: only another workspace switches. The active one can still emit
// (Ctrl/Cmd+click or a tap deselects it, value null); that is ignored.
function onWorkspaceChange({ value: ws }) {
  if (ws && !isCurrent(ws)) select(ws);
}

function select(ws) {
  store.switchWorkspace(ws);
  popoverRef.value?.hide();
  router.push({ name: 'Bots', params: { workspaceId: ws._id } });
}

// Hide the Popover before showing the Dialog. The Popover doesn't move focus when it
// closes, so the Dialog's own [autofocus] handling lands on the name field.
function openCreateModal() {
  // Focus the trigger first: the Dialog restores focus to the element focused at open,
  // and the Popover button that was clicked is gone by the time the Dialog closes.
  triggerRef.value?.$el?.focus();
  popoverRef.value?.hide();
  newName.value = '';
  showCreateModal.value = true;
}

// Cancel, X, Esc and mask click (the last three via the Dialog's update:visible)
function closeCreateModal() {
  showCreateModal.value = false;
}

async function createWorkspace() {
  if (!newName.value.trim() || creating.value) return;
  creating.value = true;
  try {
    const ws = await store.createWorkspace(newName.value.trim());
    closeCreateModal();
    router.push({ name: 'Bots', params: { workspaceId: ws._id } });
  } finally {
    creating.value = false;
  }
}

function startRename(ws) {
  renameValue.value = ws.name;
  renaming.value = true;
  nextTick(() => {
    // InputText's root element is the <input> itself
    const el = renameInputRef.value?.$el;
    el?.focus();
    el?.select();
  });
}

async function saveRename() {
  if (!renaming.value) return;
  const trimmed = renameValue.value.trim();
  if (trimmed && trimmed !== store.currentWorkspace?.name) {
    await store.renameWorkspace(store.currentWorkspace._id, trimmed);
  }
  renaming.value = false;
}

function cancelRename() {
  renaming.value = false;
}
</script>
